import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRole: string;
  fallbackPath?: string; // Optional custom fallback path
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRole, fallbackPath }) => {
  const currentUser = useSelector((state: any) => state.users?.currentUser);
  
  // REINFORCEMENT: If no user in Redux, check cache before proceeding
  // This prevents auth state flicker from causing access issues
  if (!currentUser) {
    try {
      const cachedAuth = localStorage.getItem('cachedAuth');
      if (cachedAuth) {
        const parsed = JSON.parse(cachedAuth);
        if (parsed && parsed.uid) {
          // Valid cached auth exists - wait for auth restoration
          console.log('ProtectedRoute: No Redux user but cached auth exists. Waiting for auth restoration...');
          return null; // Render nothing, wait for App.tsx to restore auth
        }
      }
    } catch (e) {
      // Parsing failed
    }
    // No cached auth either - redirect to login
    return <Navigate to="/login-page" replace />;
  }

  const selectedRole = currentUser.selectedAffiliation?.role;
  const subscriptionStatus = currentUser.subscriptionStatus;
  
  // Check subscription status (skip for owners)
  if (selectedRole !== 'owner') {
    // Strict check: Status must be explicitly 'active' or 'trial'
    // If undefined/null (no subscription record), access is denied
    const isSubscriptionActive = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
    
    // Check expiry date if available - CRITICAL FIX
    const subscriptionExpiry = currentUser.subscriptionExpiry;
    const isExpiredByDate = subscriptionExpiry ? Date.now() > subscriptionExpiry : false;
    
    const isBillingPage = window.location.pathname === '/dashboard/subscription';
    const isRegistrationPage = window.location.pathname === '/dashboard/registerschool';
    
    // Check if the user is missing a school affiliation
    const hasNoSchool = !currentUser.selectedAffiliation?.schoolID;
    const isEnrolledRole = ['school_staff', 'student', 'guardian'].includes(selectedRole);

    // NEW: If subscription status is still loading, wait before kicking the user out.
    // This fixes the "expired redirect loop" on fresh production logins.
    if (subscriptionStatus === undefined && (selectedRole === 'guardian' || selectedRole === 'admin' || selectedRole === 'school_staff' || selectedRole === 'student')) {
      console.log('ProtectedRoute: Waiting for subscription status...');
      
      // If they are explicitly navigating to the registration page, allow it to render
      // This prevents the "black page" when subscriptionStatus remains undefined due to permission errors
      if (isRegistrationPage && selectedRole === 'admin') {
        return <>{children}</>;
      }

      // If they are on the subscription/billing page, allow it to render.
      // New admins arrive here after completing school registration with status still resolving.
      if (isBillingPage && selectedRole === 'admin') {
        return <>{children}</>;
      }
      
      // If they don't have a school yet, allow them to render the layout (which will show a banner)
      if (hasNoSchool && isEnrolledRole) {
        return <>{children}</>;
      }

      return null; // Render nothing while App.tsx resolves status
    }

    // STRICT ACCESS CONTROL:
    // 1. Must have active/trial status
    // 2. Must NOT be expired (based on date)
    if ((!isSubscriptionActive || isExpiredByDate) && !isBillingPage && !isRegistrationPage && !(hasNoSchool && isEnrolledRole)) {
      console.warn(`ProtectedRoute - Access Denied: Status=${subscriptionStatus}, ExpiredByDate=${isExpiredByDate}`);
      
      // If the school isn't registered yet, redirect to the registration form instead of the "expired" page
      if ((subscriptionStatus === 'not_registered' || subscriptionStatus === undefined) && selectedRole === 'admin') {
        return <Navigate to="/dashboard/registerschool" replace />;
      }
      
      return <Navigate to="/subscription-expired" replace />;
    }
  }

  // If the user's selected role doesn't match the required role for this route, redirect them
  // Exception: admin role can access school_staff routes (admin is a superset)
  const isAllowedAccess = 
    selectedRole === allowedRole || 
    (selectedRole === 'admin' && allowedRole === 'school_staff');

  if (!isAllowedAccess) {
    console.warn(`ProtectedRoute - Access denied: User role "${selectedRole}" tried to access "${allowedRole}" route`);
    
    // Use custom fallback path if provided, otherwise redirect to role-appropriate dashboard
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }
    
    // Redirect to their appropriate dashboard based on their current role
    switch (selectedRole) {
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      case 'guardian':
        return <Navigate to="/guardian-dashboard" replace />;
      case 'school_staff':
        return <Navigate to="/faculty-dashboard" replace />;
      case 'student':
        return <Navigate to="/student-dashboard" replace />;
      case 'owner':
        return <Navigate to="/owner-dashboard" replace />;
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }

  // Role matches, allow access
  return <>{children}</>;
};

export default ProtectedRoute;
